import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { getPublicFileUrl } from "@/lib/api";
import { usePublicLang } from "@/contexts/PublicLangContext";
import { LANDING } from "@/constants/testIds";

const NAV_ITEMS = [
  { id: "tratamientos", labelKey: "landing.nav.treatments", testId: LANDING.navTreatments, key: "treatments" },
  { id: "doctora", labelKey: "landing.nav.doctor", testId: LANDING.navDoctor, key: "doctor" },
  { id: "equipo", labelKey: "landing.nav.team", testId: LANDING.navTeam, key: "team" }, // Fix AZ (2026-10-30)
  { id: "resultados", labelKey: "landing.nav.results", testId: LANDING.navResults, key: "results" },
  { id: "faq", labelKey: "landing.nav.faq", testId: LANDING.navFaq, key: "faq" },
  { id: "contacto", labelKey: "landing.nav.contact", testId: LANDING.navContact, key: "contact" },
];

export default function LandingHeader({ clinic, onBook, sectionsAvailable }) {
  const { lang, setLang, t } = usePublicLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const items = NAV_ITEMS.filter((i) => i.key === "treatments" || i.key === "contact" || sectionsAvailable[i.key]);
  // Fix BA (2026-10-30): arriba de todo el encabezado esta sobre la foto de la portada: texto claro. Al bajar, fondo marfil y texto oscuro.
  const onPhoto = !scrolled && !menuOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || menuOpen ? "bg-[#F7F3ED]/90 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
      data-testid={LANDING.header}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 md:h-20 flex items-center justify-between gap-3">
        <a href="#top" className="flex items-center gap-2 min-w-0 shrink-0" data-testid={LANDING.logo}>
          {clinic.logo_url ? (
            <img src={getPublicFileUrl(clinic.logo_url)} alt={clinic.name} className="w-9 h-9 rounded-full object-cover shrink-0" />
          ) : (
            <span className={`w-9 h-9 rounded-full flex items-center justify-center font-serif text-sm shrink-0 bg-[var(--brand-primary)] text-white`}>
              {clinic.name?.[0] || "S"}
            </span>
          )}
          {/* Fix B (2026-09-04): wordmark del manual (serif dorado, tracking
              amplio) con el descriptor debajo, en vez del nombre plano. */}
          <span className="hidden xs:flex sm:flex flex-col leading-none min-w-0">
            <span className={`font-serif text-base sm:text-xl uppercase tracking-[0.3em] truncate transition-colors ${onPhoto ? "text-white" : "text-[var(--brand-primary)]"}`}>STRANI</span>
            <span className={`text-[7px] sm:text-[8px] uppercase tracking-[0.28em] mt-1 transition-colors ${onPhoto ? "text-white/80" : "text-[#6B6058]"}`}>Just Because</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7 mx-auto">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => scrollTo(item.id)}
              className={`text-[11px] font-medium uppercase tracking-[0.16em] whitespace-nowrap hover:opacity-70 transition-[opacity,color] ${onPhoto ? "text-white" : "text-[#292522]"}`}
              data-testid={item.testId}
            >
              {t(item.labelKey)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className={`flex rounded-full border overflow-hidden text-[11px] sm:text-xs font-semibold ${onPhoto ? "border-white/40" : "border-black/10"}`}>
            <button
              type="button"
              onClick={() => setLang("es")}
              className={`px-2 sm:px-2.5 py-1.5 transition-colors ${lang === "es" ? "bg-[var(--brand-primary)] text-white" : onPhoto ? "text-white/85" : "text-[#6B6058]"}`}
              data-testid={LANDING.langEs}
            >
              ES
            </button>
            <button
              type="button"
              onClick={() => setLang("en")}
              className={`px-2 sm:px-2.5 py-1.5 transition-colors ${lang === "en" ? "bg-[var(--brand-primary)] text-white" : onPhoto ? "text-white/85" : "text-[#6B6058]"}`}
              data-testid={LANDING.langEn}
            >
              EN
            </button>
          </div>

          <button
            type="button"
            onClick={onBook}
            className="inline-flex bg-[var(--brand-accent)] text-white text-[10px] sm:text-[11px] font-medium uppercase tracking-[0.16em] px-4 sm:px-6 py-2.5 sm:py-3 rounded-md shadow-lg shadow-[rgba(129,216,208,0.45)] hover:opacity-90 active:scale-95 transition-all whitespace-nowrap animate-[floatCta_3s_ease-in-out_infinite] motion-reduce:animate-none"
            data-testid={LANDING.ctaHeader}
          >
            {t("landing.nav.cta")}
          </button>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className={`lg:hidden p-2 rounded-full ${onPhoto ? "text-white" : "text-[#292522]"}`}
            data-testid={LANDING.mobileMenuToggle}
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-[#F7F3ED] border-t border-black/5 px-5 py-5 flex flex-col gap-4" data-testid={LANDING.mobileMenu}>
          {items.map((item) => (
            <button key={item.id} type="button" onClick={() => scrollTo(item.id)} className="text-left text-base font-medium text-[#292522]" data-testid={`${item.testId}-mobile`}>
              {t(item.labelKey)}
            </button>
          ))}
        </div>
      )}
    </header>
  );
}
